import { userAnnouncementApi } from '@/user/api/announcement';
import { notificationApi } from '@/user/api/notification';
import { ref, h } from 'vue';
import GlobalChatWebSocketService, { AnnouncementMessage } from '@/user/services/globalChatService';
import { ElNotification, ElMessageBox } from 'element-plus';
import { UnreadCountMessage } from '@/user/services/globalChatService';
import router from '@/user/router';

// 注入美化滚动条的CSS样式
const injectScrollbarStyles = () => {
  if (!document.querySelector('#crochet-notification-scrollbar-styles')) {
    const style = document.createElement('style');
    style.id = 'crochet-notification-scrollbar-styles';
    style.innerHTML = `
      .crochet-notification ::-webkit-scrollbar {
        width: 6px;
      }
      
      .crochet-notification ::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
      }
      
      .crochet-notification ::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 3px;
      }
      
      .crochet-notification ::-webkit-scrollbar-thumb:hover {
        background: #a8a8a8;
      }
    `;
    document.head.appendChild(style);
  }
};

// 初始化滚动条样式
injectScrollbarStyles();

// 全局存储未读消息数量
const unreadAnnouncements = ref(0);
const unreadNotifications = ref(0);

// 定时器ID
let pollTimer: number | null = null;

// 标记是否已初始化，防止重复注册处理器
let isInitialized = false;

/**
 * 初始化WebSocket连接
 */
export const initWebSocketNotifications = () => {
  console.log('[NotificationService] Initializing WebSocket notifications');
  
  // 防止重复初始化
  if (isInitialized) {
    console.log('[NotificationService] Already initialized, skipping...');
    return;
  }
  
  // 添加通知处理器
  GlobalChatWebSocketService.addNotificationHandler(handleNotificationMessage);
  
  // 添加未读数更新处理器
  GlobalChatWebSocketService.addUnreadCountHandler(handleUnreadCountUpdate);
  
  // 添加公告处理器
  GlobalChatWebSocketService.addAnnouncementHandler(handleAnnouncementMessage);
  
  // 标记为已初始化
  isInitialized = true;
};

/**
 * 获取未读消息数量
 */
export const getUnreadCounts = async () => {
  console.log('[NotificationService] Fetching unread counts');
  try {
    // 获取未读公告数 - API调用会直接返回数字（因为request.ts中响应拦截器已处理）
    const announcementResponse: any = await userAnnouncementApi.getUnreadCount();
    console.log('[NotificationService] Announcement unread count response:', announcementResponse);
    // 根据日志显示，API返回的是对象格式，需要正确处理
    const announcementCount = typeof announcementResponse === 'number' ? announcementResponse 
      : (announcementResponse && typeof announcementResponse.data !== 'undefined' ? announcementResponse.data : 0);
    console.log('[NotificationService] Processed announcement count:', announcementCount);
    unreadAnnouncements.value = Number(announcementCount) || 0;

    // 获取未读互动消息数 - API调用会直接返回数字（因为request.ts中响应拦截器已处理）
    const notificationResponse: any = await notificationApi.getUnreadCount();
    console.log('[NotificationService] Notification unread count response:', notificationResponse);
    const notificationCount = typeof notificationResponse === 'number' ? notificationResponse 
      : (notificationResponse && typeof notificationResponse.data !== 'undefined' ? notificationResponse.data : 0);
    console.log('[NotificationService] Processed notification count:', notificationCount);
    unreadNotifications.value = Number(notificationCount) || 0;
  } catch (error) {
    console.error('[NotificationService] Error getting unread counts:', error);
  }
};

// 辅助函数，获取消息动作文本
const getMessageActionText = (type: number): string => {
  switch(type) {
    case 1:
      return '点赞了你的帖子';
    case 2:
      return '点赞了你的评论';
    case 3:
      return '评论了你的帖子';
    case 4:
      return '回复了你的评论';
    case 5:
      return '关注了你';
    case 6:
      return '收藏了你的帖子';
    default:
      return '与你产生了互动';
  }
};

/**
 * 获取通知类型对应的图标和颜色
 */
const getNotificationConfig = (type: number) => {
  switch(type) {
    case 1: // 点赞帖子
    case 2: // 点赞评论
      return { icon: '👍', color: '#e6a23c' };
    case 3: // 评论帖子
    case 4: // 回复评论
      return { icon: '💬', color: '#67c23a' };
    case 5: // 关注
      return { icon: '👤', color: '#409eff' };
    case 6: // 收藏
      return { icon: '❤️', color: '#f56c6c' };
    default:
      return { icon: '🔔', color: '#909399' };
  }
};

/**
 * WebSocket通知处理器
 */
const handleNotificationMessage = (message: any) => {
  console.log('[NotificationService] Received WebSocket notification:', message);
  
  // 检查是否是点赞、评论、回复、关注、收藏相关的通知
  if (message && (message.type >= 1 && message.type <= 6)) { // 1-点赞帖子，2-点赞评论，3-评论帖子，4-回复评论，5-关注，6-收藏帖子
    // 增加未读消息计数
    unreadNotifications.value += 1;
    
    // 获取通知配置
    const config = getNotificationConfig(message.type);
    
    // 显示Element Plus通知，使用fromUserName和content字段
    const notificationInstance = ElNotification({
      title: `${config.icon} ${message.fromUserName}`,
      message: h('div', [
        h('p', { 
          style: 'margin: 0; font-weight: 600; color: #5c5142;' 
        }, message.content || `${getMessageActionText(message.type)}`),
        h('p', { 
          style: 'margin: 4px 0 0 0; color: #909399; font-size: 12px;' 
        }, `${new Date(message.createTime).toLocaleString()}`)
      ]),
      duration: 5000,
      position: 'top-right',
      offset: 20,
      customClass: `crochet-notification crochet-notification-type-${message.type}`,
      onClick: () => {
        console.log('[NotificationService] Notification clicked, handling notification click:', message);
        // 根据消息类型处理点击事件
        handleNotificationClick(message);
      }
    });
  }
};

/**
 * 根据通知消息处理点击事件
 */
const handleNotificationClick = (message: any) => {
  // 根据消息类型决定如何处理点击事件
  switch(message.type) {
    case 1: // 点赞帖子
      // 这些类型的通知仍然跳转到帖子页面
      router.push({ path: `/post/${message.targetId}` }).catch(err => {
        console.error('[NotificationService] Router navigation error:', err);
        window.location.href = '/';
      });
      break;
    case 2: // 点赞评论
    case 3: // 评论帖子
      // 这些类型的通知仍然跳转到帖子页面
      router.push({ path: `/post/${message.targetId}` }).catch(err => {
        console.error('[NotificationService] Router navigation error:', err);
        window.location.href = '/';
      });
      break;
    case 4: // 回复评论
    case 6: // 收藏帖子
      // 这些类型的通知仍然跳转到帖子页面
      router.push({ path: `/post/${message.targetId}` }).catch(err => {
        console.error('[NotificationService] Router navigation error:', err);
        window.location.href = '/';
      });
      break;
    case 5: // 关注用户
      // 发送自定义事件，用于在当前页面打开用户详情模态框
      window.dispatchEvent(new CustomEvent('openUserProfileModal', { 
        detail: { userId: message.fromUserId } 
      }));
      break;
    default:
      // 如果类型未知，跳转到首页
      router.push({ path: '/' }).catch(err => {
        console.error('[NotificationService] Router navigation error:', err);
        window.location.href = '/';
      });
  }
};

/**
 * WebSocket公告处理器
 */
const handleAnnouncementMessage = (message: AnnouncementMessage) => {
  console.log('[NotificationService] Received WebSocket announcement:', message);
  
  // 增加未读公告计数
  unreadAnnouncements.value += 1;
  
  // 根据公告类型确定标题和图标
  let titlePrefix = '';
  switch(message.type) {
    case 1:
      titlePrefix = '📢 系统通知';
      break;
    case 2:
      titlePrefix = '🎉 活动公告';
      break;
    case 3:
      titlePrefix = '🔧 维护通知';
      break;
    case 4:
      titlePrefix = '🚀 版本更新';
      break;
    default:
      titlePrefix = '🔔 公告';
    }
    
    // 显示Element Plus通知
    const notificationInstance = ElNotification({
      title: `${titlePrefix}`,
      message: h('div', [
        h('p', { 
          style: 'margin: 0; font-weight: 600; color: #5c5142;' 
        }, message.title),
        h('p', { 
          style: `
            margin: 4px 0 0 0; 
            color: #909399; 
            font-size: 12px; 
            max-height: 80px; 
            overflow-y: auto;
            line-height: 1.4;
            padding-right: 8px;
          `
        }, message.content),
        h('p', { 
          style: 'margin: 4px 0 0 0; color: #909399; font-size: 12px;' 
        }, `${new Date(message.publishTime).toLocaleString()}`)
      ]),
      duration: 8000, // 公告可能更重要，所以持续时间稍长一些
      position: 'top-right',
      offset: 20,
      customClass: `crochet-notification crochet-announcement-type-${message.type}`,
      onClick: () => {
        console.log('[NotificationService] Announcement clicked, navigating to announcement center');
        // 点击公告跳转到公告中心
        router.push({ path: `/announcements` }).catch(err => {
          console.error('[NotificationService] Router navigation error:', err);
          window.location.href = '/announcements';
        });
      }
    });
  };

/**
 * 处理未读数更新推送
 */
const handleUnreadCountUpdate = (unreadCount: UnreadCountMessage) => {
  console.log('[NotificationService] Received unread count update:', unreadCount);
  
  // 更新本地未读数
  unreadAnnouncements.value = unreadCount.announcementCount;
  unreadNotifications.value = unreadCount.notificationCount;
  
  console.log(`[NotificationService] Updated local unread counts - Announcements: ${unreadAnnouncements.value}, Notifications: ${unreadNotifications.value}`);
};

/**
 * 开始轮询未读消息数量
 * @param interval 轮询间隔，默认30秒
 */
export const startPollingUnreadCounts = (interval = 30000) => {
  console.log('[NotificationService] Starting polling with interval:', interval);
  
  if (pollTimer) {
    console.log('[NotificationService] Clearing existing timer:', pollTimer);
    clearInterval(pollTimer);
  }

  // 立即获取一次
  getUnreadCounts();

  // 设置定时轮询
  pollTimer = window.setInterval(() => {
    console.log('[NotificationService] Polling tick');
    getUnreadCounts();
  }, interval);
};

/**
 * 停止轮询
 */
export const stopPollingUnreadCounts = () => {
  console.log('[NotificationService] Stopping polling, timer ID:', pollTimer);
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

/**
 * 手动触发获取未读消息数量
 */
export const refreshUnreadCounts = () => {
  console.log('[NotificationService] Manual refresh of unread counts');
  getUnreadCounts();
};

export {
  unreadAnnouncements,
  unreadNotifications
};